import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import InputText from '../../../../components/InputText/InputText'
import {
    validationSchema,
    getInitialValues,
} from '../../../../forms/PersonalDetailsForm'
import InputPhoneNumber from '../../../../components/InputText/components/InputPhoneNumber'
import CardInfo from '../../../../components/CardInfo/CardInfo'
import EditAction from '../EditAction'
import useScreen from '../../../../hooks/useScreen'
import { formatPhoneToInput } from '../../../../helpers/util'

const PersonalDetails = ({ user, handleSubmit }) => {
    const [isEnabledPD, setIsEnabledPD] = useState(false)
    const { isMobile } = useScreen()

    const {
        getFieldProps,
        isSubmitting,
        setSubmitting,
        isValid,
        setFieldValue,
        values,
        handleSubmit: onSubmitForm,
        errors,
        touched,
    } = useFormik({
        validationSchema,
        initialValues: getInitialValues(user),
        onSubmit: formValues => {
            setSubmitting(true)
            handleSubmit &&
                handleSubmit(formValues).then(() => {
                    setSubmitting(false)
                    setIsEnabledPD(false)
                })
        },
    })

    useEffect(() => {
        // to restart the initial value if the user cancel the edit
        if (!isEnabledPD) {
            setFieldValue('first_name', user?.first_name)
            setFieldValue('email', user?.email)
            setFieldValue('phone', formatPhoneToInput(user?.phone))
            setFieldValue('last_name', user?.last_name)
        }
    }, [isEnabledPD])

    const hasDiferences =
        JSON.stringify(values) !==
        JSON.stringify({
            first_name: user?.first_name || '',
            email: user?.email || '',
            last_name: user?.last_name || '',
            phone: formatPhoneToInput(user?.phone) || '',
        })

    const renderForm = () => {
        return (
            <div className="w-full h-auto flex flex-col items-center justify-center md:gap-y-0 gap-y-0">
                <div className="w-full flex md:flex-row flex-col items-center justify-between gap-x-6 gap-y-0">
                    <InputText
                        {...getFieldProps('first_name')}
                        placeholder="Enter first name"
                        additionalClass="w-full flex-grow"
                        additionalInputClass="h-12"
                        label="First Name"
                        disabled={!isEnabledPD}
                        error={
                            isEnabledPD &&
                            touched['first_name'] &&
                            errors['first_name']
                        }
                    />
                    <InputText
                        {...getFieldProps('last_name')}
                        placeholder="Enter last name"
                        additionalClass="w-full flex-grow"
                        label="Last Name"
                        disabled={!isEnabledPD}
                        additionalInputClass="h-12"
                        error={
                            isEnabledPD &&
                            touched['last_name'] &&
                            errors['last_name']
                        }
                    />
                </div>

                <div className="mb-6 w-full bg-grayLines h-px md:block hidden" />

                <div className="w-full flex md:flex-row flex-col items-center justify-between gap-x-6 gap-y-0">
                    <InputPhoneNumber
                        {...getFieldProps('phone')}
                        placeholder="Enter phone number"
                        additionalClass="w-full flex-grow"
                        label="Phone Number"
                        onChange={value => setFieldValue('phone', value)}
                        value={values?.phone}
                        disabled={!isEnabledPD}
                        error={
                            isEnabledPD && touched['phone'] && errors['phone']
                        }
                        className="h-12"
                    />
                    <InputText
                        {...getFieldProps('email')}
                        placeholder="Enter email"
                        error={touched['email'] && errors['email']}
                        additionalClass="w-full flex-grow"
                        label="Email Address"
                        additionalInputClass="h-12"
                        hasError={isMobile ? false : true}
                        disabled
                    />
                </div>
            </div>
        )
    }

    return (
        <form onSubmit={onSubmitForm} className="w-full">
            <CardInfo
                title="Personal Details"
                content={renderForm()}
                titleClass={isEnabledPD && isMobile && 'w-full'}
                headerActions={[
                    <EditAction
                        setEnabled={setIsEnabledPD}
                        isValid={isValid && !isSubmitting && hasDiferences}
                        key="editButton"
                    />,
                ]}
            />
        </form>
    )
}

PersonalDetails.propTypes = {
    user: PropTypes.object,
    handleSubmit: PropTypes.func,
}

export default PersonalDetails
